/**
 * Apps — the catalogue page at `/`. Everything you can use, plus
 * what you've starred and what you manage.
 *
 *   - A filter segment: All / Mine / Favorites. Persisted to
 *     localStorage per user so it survives reloads on the same browser.
 *   - A text-search input that filters within the active segment
 *     (matches name, slug, description, manager identity).
 *   - Card-as-link: the whole card opens the live app — that's the
 *     primary action, so it doesn't need a stark Open button competing
 *     with the app name. A subtle "Open ↗" hint at the bottom-right
 *     shows the affordance and brightens on hover. Manage (or Details)
 *     stays as a discreet ghost button for users who need it.
 *   - A star button per card to favorite/unfavorite (server-side via
 *     POST/DELETE /api/me/favorites/:slug). The toggle is optimistic —
 *     local state flips immediately; runAction reconciles + surfaces
 *     errors via the page-level banner.
 *   - A per-app glyph: a colored initial chip, palette index hashed
 *     from slug, so each app reads as distinct in the grid.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, apiMessage, type ApiError } from '../api/client.js';
import type { AppSummary } from '../api/types.js';
import type { MeResponse } from '../api/types.js';
import { Avatar } from '../components/Avatar.js';
import {
  IconChevronDown,
  IconExternal,
  IconRocket,
  IconSearch,
  IconSparkle,
  IconStar,
} from '../components/Icons.js';
import { StatusBadge } from '../components/StatusBadge.js';
import {
  Banner,
  ButtonLink,
  EmptyState,
  PageHeader,
  PageSkeleton,
  inputClass,
} from '../components/ui.js';
import { relTime } from '../lib/format.js';
import { runAction, type ActionMessage } from '../lib/runAction.js';
import { usePersistentState } from '../lib/usePersistentState.js';

type Filter = 'all' | 'mine' | 'favorites';

interface PersistedState {
  filter: Filter;
  q: string;
}

const FILTERS: { value: Filter; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'mine', label: 'Mine' },
  { value: 'favorites', label: 'Favorites' },
];

const DEFAULT_STATE: PersistedState = { filter: 'all', q: '' };

function isPersistedState(value: unknown): value is PersistedState {
  if (!value || typeof value !== 'object') return false;
  const v = value as Partial<PersistedState>;
  if (v.filter !== 'all' && v.filter !== 'mine' && v.filter !== 'favorites') return false;
  if (typeof v.q !== 'string') return false;
  return true;
}

type ManagerEntry = NonNullable<AppSummary['managers']>[number];

const FACEPILE_MAX = 4;

// Soft tile palette for the per-app glyph. Eight colors is enough
// variety to differentiate cards in a grid without devolving into
// chromatic chaos. Each entry pairs a translucent fill (works on
// both themes) with a higher-contrast foreground for the initial.
const GLYPH_PALETTES = [
  'bg-violet-500/12 text-violet-700 ring-violet-500/25 dark:text-violet-200 dark:ring-violet-300/25',
  'bg-blue-500/12 text-blue-700 ring-blue-500/25 dark:text-blue-200 dark:ring-blue-300/25',
  'bg-emerald-500/12 text-emerald-700 ring-emerald-500/25 dark:text-emerald-200 dark:ring-emerald-300/25',
  'bg-amber-500/15 text-amber-800 ring-amber-500/25 dark:text-amber-200 dark:ring-amber-300/25',
  'bg-rose-500/12 text-rose-700 ring-rose-500/25 dark:text-rose-200 dark:ring-rose-300/25',
  'bg-cyan-500/12 text-cyan-700 ring-cyan-500/25 dark:text-cyan-200 dark:ring-cyan-300/25',
  'bg-fuchsia-500/12 text-fuchsia-700 ring-fuchsia-500/25 dark:text-fuchsia-200 dark:ring-fuchsia-300/25',
  'bg-indigo-500/12 text-indigo-700 ring-indigo-500/25 dark:text-indigo-200 dark:ring-indigo-300/25',
];

function glyphPaletteFor(slug: string): string {
  let hash = 0;
  for (let i = 0; i < slug.length; i++) {
    hash = (hash << 5) - hash + slug.charCodeAt(i);
    hash |= 0;
  }
  return GLYPH_PALETTES[Math.abs(hash) % GLYPH_PALETTES.length]!;
}

function glyphInitial(name: string): string {
  const trimmed = name.trim();
  if (!trimmed) return '?';
  const parts = trimmed.split(/\s+/).filter(Boolean);
  if (parts.length === 1) return parts[0]!.slice(0, 1).toUpperCase();
  return (parts[0]![0]! + parts[1]![0]!).toUpperCase();
}

function ManagerFacepile({ managers }: { managers: ManagerEntry[] }) {
  if (managers.length === 0) {
    return <span className="text-faint text-xs">No manager listed</span>;
  }
  const visible = managers.slice(0, FACEPILE_MAX);
  const overflow = managers.length - visible.length;
  const overflowTitle =
    overflow > 0
      ? managers
          .slice(FACEPILE_MAX)
          .map((m) => m.name ?? m.email)
          .join(', ')
      : undefined;
  return (
    <span className="inline-flex items-center -space-x-1.5">
      {visible.map((m) => (
        <span
          key={m.user_id ?? m.email}
          title={m.name ?? m.email}
          className="ring-2 ring-surface rounded-full"
        >
          <Avatar user={m} size={22} />
        </span>
      ))}
      {overflow > 0 && (
        <span
          title={overflowTitle}
          className="ring-2 ring-surface rounded-full bg-surface-2 text-muted text-[10px] font-semibold flex items-center justify-center w-[22px] h-[22px]"
        >
          +{overflow}
        </span>
      )}
    </span>
  );
}

interface AppsState {
  loading: boolean;
  apps?: AppSummary[];
  error?: string;
}

export function Apps({ me }: { me: MeResponse }) {
  const [state, setState] = useState<AppsState>({ loading: true });
  const [persisted, setPersisted] = usePersistentState<PersistedState>(
    `kin-applist:${me.user.id}`,
    DEFAULT_STATE,
    isPersistedState,
  );
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<ActionMessage | null>(null);

  useEffect(() => {
    let cancelled = false;
    api
      .get<{ apps: AppSummary[] }>('/api/apps')
      .then((res) => {
        if (cancelled) return;
        setState({ loading: false, apps: res.apps });
      })
      .catch((err: ApiError) => {
        if (cancelled) return;
        setState({ loading: false, error: apiMessage(err) });
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const { filter, q } = persisted;

  const filtered = useMemo(() => {
    if (!state.apps) return [];
    // Server-side already excludes deleted + archived. Frozen apps
    // arrive only when the caller is a manager; we show them under
    // "Mine" only, hide elsewhere.
    let pool = state.apps;
    if (filter === 'mine') pool = pool.filter((a) => a.is_manager === true);
    else if (filter === 'favorites') pool = pool.filter((a) => a.is_favorite === true);
    if (filter !== 'mine') pool = pool.filter((a) => a.status !== 'frozen');

    const needle = q.trim().toLowerCase();
    if (!needle) return pool;
    return pool.filter((a) => {
      const haystack = [
        a.name,
        a.slug,
        a.description ?? '',
        ...(a.managers ?? []).flatMap((m) => [m.name ?? '', m.email]),
      ]
        .join(' ')
        .toLowerCase();
      return haystack.includes(needle);
    });
  }, [state.apps, filter, q]);

  function setFilter(f: Filter) {
    setPersisted((p) => ({ ...p, filter: f }));
  }
  function setQ(next: string) {
    setPersisted((p) => ({ ...p, q: next }));
  }

  async function toggleFavorite(app: AppSummary) {
    if (!state.apps) return;
    const nextIsFavorite = !app.is_favorite;
    // Optimistic: flip locally first, reconcile on response.
    setState((s) =>
      s.apps
        ? {
            ...s,
            apps: s.apps.map((a) => (a.id === app.id ? { ...a, is_favorite: nextIsFavorite } : a)),
          }
        : s,
    );
    type FavoriteResponse = { ok: true; is_favorite: boolean };
    const endpoint = `/api/me/favorites/${encodeURIComponent(app.slug)}`;
    await runAction({
      do: () =>
        nextIsFavorite
          ? api.post<FavoriteResponse>(endpoint)
          : api.delete<FavoriteResponse>(endpoint),
      onSuccess: () => '',
      errorPrefix: `Couldn't ${nextIsFavorite ? 'favorite' : 'unfavorite'} ${app.name}`,
      setBusy,
      setMessage,
    });
    // Empty onSuccess message suppresses the banner on the happy path —
    // the star already moved, no need for a "Saved" toast.
  }

  if (state.loading && !state.apps) return <PageSkeleton rows={5} />;

  return (
    <div className="animate-fade-in">
      <PageHeader
        title="Apps"
        description="Everything you can use, plus what you've starred and what you manage."
        actions={<StartBuildingButton />}
      />

      {state.error && <Banner tone="error">{state.error}</Banner>}
      {message && (
        <Banner tone={message.tone} onDismiss={() => setMessage(null)}>
          {message.text}
        </Banner>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-3 mb-5">
        <div className="relative">
          <IconSearch
            size={16}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-faint pointer-events-none"
          />
          <input
            type="search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Filter by name, description, or who's responsible…"
            aria-label="Filter apps"
            className={`${inputClass} pl-9`}
          />
        </div>
        <div className="inline-flex rounded-lg border border-line bg-surface p-0.5">
          {FILTERS.map((f) => {
            const active = filter === f.value;
            return (
              <button
                key={f.value}
                type="button"
                onClick={() => setFilter(f.value)}
                aria-pressed={active}
                className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/40 ${
                  active ? 'bg-brand text-brand-fg' : 'text-muted hover:text-ink'
                }`}
              >
                {f.label}
              </button>
            );
          })}
        </div>
      </div>

      {filtered.length === 0 ? (
        <FilterEmptyState filter={filter} q={q} hasAnyApps={(state.apps?.length ?? 0) > 0} />
      ) : (
        <ul className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {filtered.map((app) => (
            <li key={app.id}>
              <AppCard app={app} onToggleFavorite={toggleFavorite} busy={busy} />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function AppCard({
  app,
  onToggleFavorite,
  busy,
}: {
  app: AppSummary;
  onToggleFavorite: (app: AppSummary) => void;
  busy: boolean;
}) {
  const liveUrl = app.cf_custom_domain_live ? `https://${app.cf_custom_domain_live}` : null;
  const showStatus = app.status !== 'active';
  const manageLabel = app.is_manager ? 'Manage' : 'Details';
  const detailPath = `/apps/${app.slug}`;
  const isFavorite = app.is_favorite === true;
  const glyphClasses = glyphPaletteFor(app.slug);
  const initial = glyphInitial(app.name);

  return (
    <article
      className={`group relative h-full rounded-xl border border-line bg-surface p-5 flex flex-col gap-4 transition-all duration-200 ease-snappy ${
        liveUrl
          ? 'hover:border-brand/40 hover:shadow-elevated hover:-translate-y-0.5'
          : 'hover:border-line-strong'
      }`}
    >
      {/*
       * Stretched link — the entire card surface opens the live app.
       * Sits at z-[1] beneath the interactive controls (star, manage)
       * which get relative z-[2] so they stay clickable.
       */}
      {liveUrl && (
        <a
          href={liveUrl}
          target="_blank"
          rel="noreferrer"
          aria-label={`Open ${app.name}`}
          className="absolute inset-0 z-[1] rounded-xl focus:outline-none focus-visible:ring-2 focus-visible:ring-brand/60 focus-visible:ring-offset-2 focus-visible:ring-offset-bg"
        >
          <span className="sr-only">Open {app.name}</span>
        </a>
      )}

      <button
        type="button"
        onClick={() => onToggleFavorite(app)}
        disabled={busy}
        aria-pressed={isFavorite}
        aria-label={`${isFavorite ? 'Unfavorite' : 'Favorite'} ${app.name}`}
        title={isFavorite ? 'Unfavorite' : 'Favorite'}
        className={`absolute top-3 right-3 z-[2] inline-flex items-center justify-center h-8 w-8 rounded-full transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/40 ${
          isFavorite
            ? 'text-amber-500 hover:bg-amber-500/10'
            : 'text-faint opacity-0 group-hover:opacity-100 focus-visible:opacity-100 hover:text-amber-500 hover:bg-surface-2'
        }`}
      >
        <IconStar size={15} filled={isFavorite} />
      </button>

      <div className="flex items-start gap-3.5">
        <span
          aria-hidden
          className={`flex-none h-11 w-11 rounded-lg ring-1 ring-inset flex items-center justify-center font-display font-semibold text-base tracking-tight ${glyphClasses}`}
        >
          {initial}
        </span>
        <div className="min-w-0 flex-1 pr-9">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-display font-semibold text-lg text-ink tracking-tight truncate transition-colors group-hover:text-brand">
              {app.name}
            </h3>
            {showStatus && (
              <span className="relative z-[2]">
                <StatusBadge status={app.status} />
              </span>
            )}
          </div>
          {app.description && (
            <p className="text-muted text-sm mt-1 line-clamp-2 leading-snug">{app.description}</p>
          )}
        </div>
      </div>

      <div className="mt-auto flex items-center justify-between gap-3 text-xs text-muted pt-4 border-t border-line">
        <div className="relative z-[2] min-w-0">
          <ManagerFacepile managers={app.managers ?? []} />
        </div>
        <span className="shrink-0 inline-flex items-center gap-1.5">
          <span>Updated {relTime(app.updated_at ?? app.created_at)}</span>
          {app.visibility === 'workspace' && (
            <>
              <span aria-hidden>·</span>
              <span className="text-info">Mytra-wide</span>
            </>
          )}
        </span>
      </div>

      <div className="flex items-center justify-between gap-2 -mt-1">
        <ButtonLink
          to={detailPath}
          variant="ghost"
          size="sm"
          className="relative z-[2] -ml-2 text-muted hover:text-ink"
        >
          {manageLabel}
        </ButtonLink>
        {liveUrl ? (
          <span
            aria-hidden
            className="inline-flex items-center gap-1 text-xs font-semibold text-muted transition-colors group-hover:text-brand"
          >
            Open
            <IconExternal
              size={11}
              className="transition-transform duration-200 ease-snappy group-hover:translate-x-0.5"
            />
          </span>
        ) : (
          <span className="text-xs text-faint italic">Not published</span>
        )}
      </div>
    </article>
  );
}

function FilterEmptyState({
  filter,
  q,
  hasAnyApps,
}: {
  filter: Filter;
  q: string;
  hasAnyApps: boolean;
}) {
  if (q.trim().length > 0) {
    return (
      <EmptyState
        title={`No apps match "${q}"`}
        description="Try a shorter search or switch the filter above."
      />
    );
  }
  if (filter === 'favorites') {
    return (
      <EmptyState
        title="No favorites yet"
        description="Click the star on any card to pin it here."
      />
    );
  }
  if (filter === 'mine') {
    return (
      <EmptyState
        title="No apps you manage"
        description="Apps you create or get added to as a manager will show up here."
      />
    );
  }
  if (!hasAnyApps) {
    return (
      <EmptyState
        icon={<IconSparkle size={18} />}
        title="Nothing here yet"
        description="Open Claude (or any AI that speaks MCP) signed in with your Mytra account and ask it to build something. Your first app shows up here as soon as it's provisioned."
        action={
          <Link to="/docs" className="font-medium text-brand hover:underline">
            Start in docs →
          </Link>
        }
      />
    );
  }
  return <EmptyState title="No apps match" description="Try a different filter." />;
}

/**
 * Split-button used in the Apps page header.
 *
 *   ┌──────────────────────┬─────┐
 *   │  🚀 Start building   │  ▼  │
 *   └──────────────────────┴─────┘
 *
 * The primary half goes to the docs (the existing default behavior).
 * The caret half opens a small menu with "Create app", which links
 * to the new-app form at `/apps/new`. Click-outside / Esc dismisses
 * the menu. Kept inline rather than promoted to `ui.tsx` because we
 * only need a split button in this one place — promoting the
 * abstraction would cost more than it saves.
 */
function StartBuildingButton() {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  // Click outside / Esc → close. Only registered while the menu is open.
  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e: PointerEvent) => {
      if (!wrapperRef.current) return;
      if (wrapperRef.current.contains(e.target as Node)) return;
      setOpen(false);
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  return (
    <div ref={wrapperRef} className="relative inline-flex">
      <ButtonLink
        to="/docs"
        variant="primary"
        size="sm"
        className="rounded-r-none border-r border-brand-fg/20"
      >
        <IconRocket size={14} />
        Start building
      </ButtonLink>
      <button
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="More build options"
        onClick={() => setOpen((v) => !v)}
        className={
          'h-8 px-2 inline-flex items-center justify-center rounded-r-lg font-medium ' +
          'bg-brand text-brand-fg hover:bg-brand-hover transition-colors ' +
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/50 focus-visible:ring-offset-2 focus-visible:ring-offset-bg'
        }
      >
        <IconChevronDown size={14} />
      </button>
      {open && (
        <div
          role="menu"
          className="absolute right-0 top-full mt-1.5 w-56 rounded-lg border border-line bg-surface shadow-lg z-20 py-1 animate-fade-in"
        >
          <Link
            to="/apps/new"
            role="menuitem"
            onClick={() => setOpen(false)}
            className="flex items-start gap-3 px-3 py-2 text-sm text-ink hover:bg-surface-2 transition-colors"
          >
            <IconSparkle size={14} className="text-brand mt-0.5 shrink-0" />
            <span>
              <span className="block font-medium">Create app</span>
              <span className="block text-xs text-muted mt-0.5">
                Name it and start chatting with Kin
              </span>
            </span>
          </Link>
        </div>
      )}
    </div>
  );
}
